import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './FeedItem.styled';
import { PlaceholderAvatar, Avatar as StyledAvatar } from '../ActivityFeed.styled';
import { FeedItemFooter } from './FeedItemFooter';
import { timeAgo } from '../timeAgo';
import { VisuallyHiddenText } from '../../../styles/mixins/visually-hidden';
import { ActivityFeedWhammyDispatchContext } from '../ActivityFeedContext';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Represents any feed item (auto-generated, user comments, and replies).
 *
 * When rendering an auto-generated feed item or user comment,
 * - it features "like", "reply", and "delete" buttons.
 * - it may have an attached list of replies.
 *
 * When rendering a reply,
 * - it features "like", and "delete" buttons.
 */
export const FeedItem = ({
  id,
  parentFeedItemId,
  type,
  name,
  agentId,
  created,
  avatar,
  content,
  likes,
  replies,
  repliesCount = 0,
  repliesVisible = false,
  dispatch,
  blockState,
  enableLikes = false,
  enableComments = false,
  loggedInMember,
  onLogin,
  isAnonymous = false,
  transitionType
}) => {
  const whammyDispatch = React.useContext(ActivityFeedWhammyDispatchContext);
  const {
    t
  } = useTranslation('blocks.activityFeed');
  const divRef = React.useRef(null);
  if (!whammyDispatch) {
    throw new Error('whammyDispatch must be set');
  }
  const createdDate = new Date(created);
  const timeAgoObj = timeAgo(createdDate);
  const isReply = type === 'reply';
  const hasReplies = Array.isArray(replies);
  const isDeletable = (type === 'comment' || type === 'reply') && (loggedInMember?.canDeleteComments || loggedInMember?.id === agentId);

  // Hide reply button if 1) not logged-in, or 2) logged-in and feed item is anonymous
  const hideReplyButton = typeof loggedInMember === 'undefined' || !loggedInMember.id || isAnonymous;
  const handleShowReplies = () => {
    if (blockState.expandedReplies.indexOf(id) !== -1) {
      /**
       * Bail if replies are already expanded to prevent uneccessary API calls.
       * At the moment, when replies are shown, we show all of them. If the user adds a reply, it
       * is prepended to the list. If replies are added by other users, they won't appear until
       * the page is refreshed.
       */
      return;
    }

    /**
     * Feed items automatically contain the latest two replies. Show them immediately.
     */
    dispatch({
      type: 'SHOW_REPLIES',
      feedItemId: id
    });

    /**
     * Fetch the rest of the replies if there are more than two. By design, we don't show a
     * loader during fetch.
     */
    if (repliesCount > 2) {
      try {
        whammyDispatch({
          type: 'GET_REPLIES',
          feedItemId: id
        });
      } catch (e) {
        /**
         * If there is an error, nothing to fallback to, just silently fail.
         * TODO show error message, no designs yet (toast?)
         */
      }
    }
  };

  /**
   * Animate the feed item when it is inserted or deleted.
   * The `transitionType` prop will indicate the type of animation to apply.
   * A ref is used to apply a CSS class to kick off the animation.
   *
   * Deleting an item is a two-step process. While the item is deleted immediately (from APIv2), we
   * do not remove it from the state, otherwise, we won't be able to animate it because it won't
   * actually exist in the DOM anymore. When the animation finishes, we dispatch whammyAction to
   * finally remove the item from state.
   */
  React.useEffect(() => {
    const node = divRef.current;
    if (!node || !transitionType) {
      return () => {};
    }
    if (transitionType === 'insert') {
      node?.classList.add('fade-in');
    } else if (transitionType === 'delete') {
      node?.classList.add('fade-out');
      node.addEventListener('animationend', () => {
        let payload = {
          type: 'FINISH_DELETE_USER_COMMENT',
          feedItemId: id
        };
        if (isReply && typeof parentFeedItemId !== 'undefined') {
          payload = {
            type: 'FINISH_DELETE_REPLY',
            parentFeedItemId,
            replyId: id
          };
        }
        whammyDispatch(payload);
      });
    }
    return () => {
      node?.classList.remove('fade-out');
      node?.classList.remove('fade-in');
    };
  });
  return /*#__PURE__*/_jsxs(Styled.FeedItemContainer, {
    tabIndex: 0,
    ref: divRef,
    "data-id": isReply ? 'cp-activity-feed-reply' : 'cp-activity-feed-comment',
    children: [/*#__PURE__*/_jsx(Styled.AvatarContainer, {
      children: avatar ? /*#__PURE__*/_jsx(StyledAvatar, {
        src: avatar,
        width: isReply ? '32' : '36',
        height: isReply ? '32' : '36',
        alt: ""
      }) : /*#__PURE__*/_jsx(PlaceholderAvatar, {})
    }), /*#__PURE__*/_jsxs(Styled.ContentContainer, {
      children: [/*#__PURE__*/_jsxs(Styled.Header, {
        children: [/*#__PURE__*/_jsx(Styled.Name, {
          children: name
        }), /*#__PURE__*/_jsxs(Styled.Time, {
          dateTime: created,
          children: [/*#__PURE__*/_jsx("span", {
            "aria-hidden": true,
            children: timeAgoObj.short
          }), /*#__PURE__*/_jsx(VisuallyHiddenText, {
            children: timeAgoObj.full
          })]
        })]
      }), /*#__PURE__*/_jsx(Styled.Content, {
        dangerouslySetInnerHTML: {
          __html: content
        }
      }), /*#__PURE__*/_jsx(FeedItemFooter, {
        id: id,
        parentFeedItemId: parentFeedItemId,
        type: type,
        loggedInMember: loggedInMember,
        onLogin: onLogin,
        enableLikes: enableLikes,
        likes: likes,
        enableComments: enableComments,
        isDeletable: isDeletable
        // The following are only used when replying to a top-level feed item
        ,
        feedItemPreview: !isReply ? {
          name,
          created,
          content,
          avatar
        } : undefined,
        isReplyable: !isReply && !hideReplyButton,
        isLikeable: !isAnonymous,
        onReplySubmitted: handleShowReplies
      }), hasReplies && replies?.length > 0 && /*#__PURE__*/_jsxs(Styled.RepliesContainer, {
        children: [!repliesVisible && /*#__PURE__*/_jsx(Styled.ShowRepliesButton, {
          onClick: handleShowReplies,
          children: t('FeedItem.showRepliesButton', {
            count: repliesCount,
            defaultValue: '{{count}} Replies'
          })
        }), repliesVisible && replies.map(reply => /*#__PURE__*/_jsx(FeedItem, {
          id: reply.id,
          parentFeedItemId: reply.parentFeedItemId,
          type: reply.type,
          name: reply.name,
          agentId: reply.agentId,
          avatar: reply.avatar,
          created: reply.created,
          content: reply.content,
          likes: reply.likes,
          dispatch: dispatch,
          blockState: blockState,
          enableLikes: enableLikes,
          enableComments: enableComments,
          loggedInMember: loggedInMember,
          transitionType: reply.transitionType
        }, reply.id))]
      })]
    })]
  });
};